import type { Spell } from '../../schemas/spell';

/**
 * Spell list del Chierico — Orazioni (L0), Livelli 1, 3, 5, 7, 9 di tutte
 * le 5 Tradizioni Sacre (manuale V1, sezione "Lista degli Incantesimi"
 * rr. 6219+, indici Tradizione-→-spell rr. 6187-6209).
 *
 * **Scope**:
 * - 3 orazioni L0 nuove del Chierico
 * - 13 incantesimi L1 nuovi
 * - 10 incantesimi L3 (accessibili al PG Chierico di livello ≥ 3)
 * - 8 incantesimi L5 (accessibili dal livello 5)
 * - **6 incantesimi L7** (D4 — accessibili dal livello 7)
 * - **5 incantesimi L9** (D4 — accessibili dal livello 9)
 *
 * Gli altri spell condivisi (es. con Mago) sono **estesi in `mago.ts`/
 * `druido.ts`/`warlock.ts`** aggiungendo entry Chierico in `availableTo`
 * (pattern additivo, niente refactor degli ID).
 *
 * **Convenzioni**:
 * - ID `chierico.<kebab-name>` per gli spell appartenenti primariamente
 *   al Chierico
 * - `availableTo` lista TUTTE le Tradizioni che danno accesso allo spell
 *   (es. *Curare Ferite Leggere* è in tutte le 5 Tradizioni)
 * - *Voce della Terra*: 5 entry `availableTo` (una per Tradizione), tutte
 *   con `requiresArchetype: 'nano'` (manuale r. 2674)
 * - *Curare Ferite Moderate*: prerequisito mossa *Primo Soccorso
 *   Migliorato* gestito in **description** (FI-24 traccia il refactor
 *   strutturato come `SpellAccess.requiresMove`)
 * - *Teschio Fluttuante* (L3, Negromanzia): description recuperata da
 *   Francesco (D3-Q7) perché mancante nella "Lista degli Incantesimi"
 *   del manuale V1 (refuso del manuale, tracciato come gap)
 * - *Visioni Attraverso il Tempo* (L3, Illuminazione + Warlock): la
 *   tabella indice del Warlock (r. 6211) lo elenca sotto L5 ma il
 *   livello canonico è 3 (D3-Q5, confermato da Francesco). Il Warlock
 *   accede via `availableTo` con `level: 3`
 */

const ALL_TRADITIONS = [
  'crepuscolo',
  'guarigione',
  'illuminazione',
  'negromanzia',
  'valore',
] as const;

/**
 * Tradizioni del Chierico che il Paladino condivide (Negromanzia/Valore).
 * Helper per le entry di `availableTo` del Paladino su spell condivisi.
 *
 * **NB**: la Tradizione **Natura** del Paladino esiste solo se archetipo
 * Elfo, e attinge alla lista del Druido — gestita separatamente in
 * `druido.ts` e in `mago.ts` (per spell del Mago condivisi col Druido come
 * Luce, Allarme, Invisibilità).
 */
const PALADIN_TRADITIONS_FROM_CHIERICO = ['negromanzia', 'valore'] as const;

// ============================================================================
// Orazioni (Livello 0) — 3 nuovi
// ============================================================================

const guida: Spell = {
  id: 'chierico.guida',
  name: 'Guida',
  level: 0,
  tags: [],
  description:
    "Il simbolo del tuo dio o del tuo patrono appare per un solo istante innanzi a te, indicandoti la direzione o il modo d'agire che esso vorrebbe tu seguissi. Il messaggio si manifesta solo tramite un segno: la comunicazione attraverso questo incantesimo è decisamente limitata.",
  availableTo: [
    ...ALL_TRADITIONS.map((t) => ({ classId: 'chierico' as const, tradizione: t })),
    ...PALADIN_TRADITIONS_FROM_CHIERICO.map((t) => ({
      classId: 'paladino' as const,
      tradizione: t,
    })),
    // Lista del Warlock (manuale r. 6211, Suppliche L0).
    { classId: 'warlock' as const },
  ],
};

const purificare: Spell = {
  id: 'chierico.purificare',
  name: 'Purificare',
  level: 0,
  tags: [],
  description:
    'Tocchi cibo, acqua o un piccolo oggetto, e lo purifichi da contaminazioni naturali (veleni comuni, marciume, sporcizia, batteri). Non funziona contro contaminazioni magiche o estremamente potenti.',
  availableTo: [
    ...ALL_TRADITIONS.map((t) => ({ classId: 'chierico' as const, tradizione: t })),
    { classId: 'druido' as const },
    ...PALADIN_TRADITIONS_FROM_CHIERICO.map((t) => ({
      classId: 'paladino' as const,
      tradizione: t,
    })),
    // Tradizione Natura del Paladino (solo Elfo) attinge alla lista del Druido,
    // che include *Purificare*.
    { classId: 'paladino' as const, tradizione: 'natura' as const, requiresArchetype: 'elfo' },
  ],
};

const voceDellaTerra: Spell = {
  id: 'chierico.voce-della-terra',
  name: 'Voce della Terra',
  level: 0,
  tags: [],
  description:
    'Percepisci eco dal passato di formazioni rocciose naturali attraverso le radici profonde che ti legano alla terra. Il GM risponderà in modo veritiero a tre domande che poni, tenendo conto delle esperienze che la pietra ha "vissuto" attraverso i suoi "sensi" e con la propria percezione del tempo. Per quanto la terra possa volerti parlare, il suo ritmo le impedisce di farlo troppo spesso o rapidamente.',
  // 5 entry per il Chierico (una per Tradizione) + 2 per il Paladino
  // (Negromanzia/Valore), tutte con `requiresArchetype: 'nano'`.
  availableTo: [
    ...ALL_TRADITIONS.map((t) => ({
      classId: 'chierico' as const,
      tradizione: t,
      requiresArchetype: 'nano',
    })),
    ...PALADIN_TRADITIONS_FROM_CHIERICO.map((t) => ({
      classId: 'paladino' as const,
      tradizione: t,
      requiresArchetype: 'nano',
    })),
  ],
};

// ============================================================================
// Livello 1 — 13 nuovi
// ============================================================================

const armaMagica: Spell = {
  id: 'chierico.arma-magica',
  name: 'Arma Magica',
  level: 1,
  tags: ['continuato'],
  description:
    "Finché non dissolvi questo effetto o non usi nuovamente la mossa *Mana* per ottenere prese, l'arma che tenevi in mano al momento del lancio ottiene l'etichetta +1d4 danni. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const benedizione: Spell = {
  id: 'chierico.benedizione',
  name: 'Benedizione',
  level: 1,
  tags: ['continuato'],
  description:
    "Scegli un alleato che stia combattendo e che puoi vedere (incluso te stesso), e indica una mossa. Finché la battaglia perdura e lui continua a combattere, l'alleato è pervaso dalla benevolenza della tua divinità e prende +1 continuato a tutti i tiri per usare la mossa che hai indicato. Mentre l'effetto è attivo, subisci -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'guarigione' },
    { classId: 'chierico', tradizione: 'valore' },
    // Paladino: solo Valore (no Guarigione tra le 3 Tradizioni del Paladino).
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const controllareNonMorti: Spell = {
  id: 'chierico.controllare-non-morti',
  name: 'Controllare Non Morti',
  level: 1,
  tags: ['continuato'],
  description:
    "Una creatura non morta e non senziente entro distanza *media* da te obbedisce ai tuoi comandi verbali al meglio delle sue limitate possibilità. L'effetto dura finché il bersaglio non subisce danni, dai un ordine autodistruttivo, usi nuovamente la mossa *Mana* per ottenere prese, o poni termine al controllo. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
    // Lista del Warlock (manuale r. 6211, L1).
    { classId: 'warlock' },
  ],
};

const coraggio: Spell = {
  id: 'chierico.coraggio',
  name: 'Coraggio',
  level: 1,
  tags: ['continuato'],
  description:
    "Un alleato che può sentire la tua voce, incluso te stesso, è infuso di grande coraggio. Finché l'incantesimo perdura il bersaglio non subisce gli effetti della paura o del terrore. Inoltre, se *sfida il pericolo*, prende +1 al tiro, ma in tal caso la magia termina. L'effetto svanisce anche se usi la mossa *Mana* per ottenere prese. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'illuminazione' },
    { classId: 'chierico', tradizione: 'valore' },
    // Paladino: solo Valore (no Illuminazione tra le 3 Tradizioni del Paladino).
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const curareFeriteLeggere: Spell = {
  id: 'chierico.curare-ferite-leggere',
  name: 'Curare Ferite Leggere',
  level: 1,
  tags: [],
  description:
    "Guarisci te stesso o un alleato che stai toccando di 1d8 PF. Le ferite superficiali si rimarginano e il dolore, almeno per un po', è lenito.",
  availableTo: [
    ...ALL_TRADITIONS.map((t) => ({ classId: 'chierico' as const, tradizione: t })),
    { classId: 'druido' as const },
    ...PALADIN_TRADITIONS_FROM_CHIERICO.map((t) => ({
      classId: 'paladino' as const,
      tradizione: t,
    })),
    { classId: 'paladino' as const, tradizione: 'natura' as const, requiresArchetype: 'elfo' },
  ],
};

const curareFeriteModerate: Spell = {
  id: 'chierico.curare-ferite-moderate',
  name: 'Curare Ferite Moderate',
  level: 1,
  tags: [],
  description:
    "Guarisci te stesso o un alleato che stai toccando di 2d8 PF. Contusioni e tagli vengono curati, il dolore che causavano è lenito, e le emorragie sono interrotte.\n\n**Richiede la mossa del Chierico *Primo Soccorso Migliorato***. L'orazione viene aggiunta alla Tradizione Sacra del PG solo se ha già preso la mossa abilitante (di livello 6-10).",
  // D9 — FI-24: `requiresMove` strutturato. L'orazione è disponibile per le
  // Tradizioni del Chierico (e per Druido/Paladino che ne ereditano l'accesso)
  // solo se il PG ha la mossa avanzata `chierico.primo-soccorso-migliorato`.
  availableTo: [
    ...ALL_TRADITIONS.map((t) => ({
      classId: 'chierico' as const,
      tradizione: t,
      requiresMove: 'chierico.primo-soccorso-migliorato',
    })),
    { classId: 'druido' as const, requiresMove: 'chierico.primo-soccorso-migliorato' },
    ...PALADIN_TRADITIONS_FROM_CHIERICO.map((t) => ({
      classId: 'paladino' as const,
      tradizione: t,
      requiresMove: 'chierico.primo-soccorso-migliorato',
    })),
    {
      classId: 'paladino' as const,
      tradizione: 'natura' as const,
      requiresArchetype: 'elfo',
      requiresMove: 'chierico.primo-soccorso-migliorato',
    },
  ],
};

const curareVeleni: Spell = {
  id: 'chierico.curare-veleni',
  name: 'Curare Veleni',
  level: 1,
  tags: [],
  description:
    'Tocchi un alleato (incluso te stesso) e lo curi da un veleno che lo sta affliggendo. I veleni comuni sono neutralizzati, gli effetti di quelli magici o estremamente potenti, invece, potrebbero essere solo sospesi temporaneamente (a discrezione del GM).',
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const incuterePaura: Spell = {
  id: 'chierico.incutere-paura',
  name: 'Incutere Paura',
  level: 1,
  tags: ['continuato'],
  description:
    "Scegli una creatura senziente che puoi vedere e un oggetto nelle vicinanze. Finché mantieni attivo l'effetto il bersaglio è spaventato da quell'oggetto e reagirà fuggendo, facendosi prendere dal panico, supplicandoti o tentando di sbarazzarsi di esso (a discrezione del GM). L'incantesimo termina se decidi di porvi fine o se usi nuovamente la mossa *Mana*. Mentre l'effetto è attivo, hai -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'crepuscolo' },
    { classId: 'chierico', tradizione: 'negromanzia' },
    // Paladino: solo Negromanzia (no Crepuscolo tra le 3 Tradizioni del Paladino).
    { classId: 'paladino', tradizione: 'negromanzia' },
    // Lista del Warlock (manuale r. 6211, Suppliche L0).
    { classId: 'warlock' },
  ],
};

const individuareAllineamento: Spell = {
  id: 'chierico.individuare-allineamento',
  name: 'Individuare Allineamento',
  level: 1,
  tags: [],
  description:
    "Pronuncia un allineamento (Buono, Legale, Neutrale, Caotico, Malvagio). Per qualche istante percepisci tutte le creature di quell'allineamento entro portata *vicino* da te.",
  availableTo: [
    { classId: 'chierico', tradizione: 'illuminazione' },
    { classId: 'druido' },
    // Tradizione Natura del Paladino (Elfo) attinge alla lista del Druido.
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
    // Lista del Warlock (manuale r. 6211, L1).
    { classId: 'warlock' },
  ],
};

const parlareConIMorti: Spell = {
  id: 'chierico.parlare-con-i-morti',
  name: 'Parlare con i Morti',
  level: 1,
  tags: [],
  description:
    'Indica una salma che puoi vedere o toccare. Il defunto risponde a una tua singola domanda al meglio della sua memoria — i morti ricordano in modo distorto, e potrebbero non sapere o non voler dire la verità. La salma deve avere ancora la lingua intatta o equivalente.',
  availableTo: [
    { classId: 'chierico', tradizione: 'guarigione' },
    { classId: 'chierico', tradizione: 'negromanzia' },
    // Paladino: solo Negromanzia (no Guarigione tra le 3 Tradizioni del Paladino).
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

const santuario: Spell = {
  id: 'chierico.santuario',
  name: 'Santuario',
  level: 1,
  tags: ['continuato'],
  description:
    "Al momento del lancio percorri il perimetro di un'area, consacrandola alla tua divinità: se sei vigile, percepisci quando qualcuno agisce con malizia al suo interno o se vi entra con cattive intenzioni. Inoltre, chiunque venga curato all'interno del Santuario, recupera +1d4 PF. L'incantesimo ha immediatamente fine se lasci l'area consacrata. Mentre mantieni l'effetto, subisci -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const scudoUltraterreno: Spell = {
  id: 'chierico.scudo-ultraterreno',
  name: 'Scudo Ultraterreno',
  level: 1,
  tags: ['continuato'],
  description:
    "Un alleato che tocchi (incluso te stesso) viene avvolto da un'aura protettiva. Finché l'effetto persiste, il bersaglio ottiene +1 Armatura. L'incantesimo termina se decidi di dissolverlo, se usi la mossa *Mana* per ottenere prese, o se il bersaglio compie un'azione contraria alla tua divinità. Mentre l'effetto è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
    // Lista del Warlock (manuale r. 6211, L1).
    { classId: 'warlock' },
  ],
};

const toccoSpettrale: Spell = {
  id: 'chierico.tocco-spettrale',
  name: 'Tocco Spettrale',
  level: 1,
  tags: [],
  description:
    "Una mano spettrale sottrae l'energia vitale di una creatura vivente entro portata *lunga* da te, infliggendole 1d4 danni *ignora armatura*, e tu recuperi un numero di PF pari ai danni *effettivamente* inflitti al bersaglio. Eventuali bonus alla cura dovuti a mosse o altri incantesimi non si applicano alla guarigione ricevuta.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

// ============================================================================
// Livello 3 — 10 nuovi (D3)
// ============================================================================

const animareIMorti: Spell = {
  id: 'chierico.animare-i-morti',
  name: 'Animare i Morti',
  level: 3,
  tags: ['continuato'],
  description:
    "Invochi uno spirito affinché prenda possesso di un corpo morto di recente e ti serva. Ciò crea uno zombie che esegue *senza discutere* i tuoi ordini al meglio delle sue limitate facoltà. Considera lo zombie come se fosse un gregario di livello 2, senza alcun Costo. Ha un modificatore di +1 a tutte le Caratteristiche, 1 PF, Carico 6, e infligge 1d6 danni. Lo zombie ottiene anche 1d4 tratti a tua scelta tra:\n\n• è abile: uno dei modificatori di Caratteristica è +2 anziché +1;\n• è resistente: ha +2 PF per ogni tuo livello da incantatore (al massimo +20 PF);\n• ha un cervello funzionante e può eseguire compiti complessi;\n• non ha l'aspetto di un cadavere, almeno per un paio di giorni.\n\nLo zombie perdura finché non viene distrutto esaurendo i suoi PF o non poni termine all'effetto. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
    { classId: 'warlock' },
  ],
};

const auraDiVerita: Spell = {
  id: 'chierico.aura-di-verita',
  name: 'Aura di Verità',
  level: 3,
  tags: ['continuato'],
  description:
    "Tutti quelli che si trovano nelle tue immediate vicinanze (nemici e alleati) non possono mentire finché questo effetto rimane attivo, ma potrebbero sempre rifiutarsi di parlare. L'incantesimo termina appena dici una menzogna (incluso il bluff, dire mezze verità con l'intento di ingannare, esagerare, dire bugie a fin di bene e così via), se decidi di dissolverlo, o se usi nuovamente la mossa *Mana* per ottenere prese. Mentre l'effetto è attivo prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'chierico', tradizione: 'illuminazione' }],
};

const bloccarePersona: Spell = {
  id: 'chierico.bloccare-persona',
  name: 'Bloccare Persona',
  level: 3,
  tags: ['continuato'],
  description:
    "Scegli una persona (non un animale o un mostro) entro gittata *vicino*. Finché mantieni l'incantesimo, essa non potrà compiere alcun movimento, ma sarà ancora in grado di parlare e di far ricorso a ogni sua capacità mentale o legata alla voce. L'effetto termina se il bersaglio subisce qualsiasi tipo di danno, lo perdi di vista, lanci un incantesimo, o decidi di porvi fine.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'valore' },
    { classId: 'warlock' },
  ],
};

const camminareConGliSpiriti: Spell = {
  id: 'chierico.camminare-con-gli-spiriti',
  name: 'Camminare con gli Spiriti',
  level: 3,
  tags: ['continuato'],
  description:
    "Tu o un alleato che stai toccando mentre lanci questo incantesimo venite circondati da invisibili spiriti della natura. Finché non decidi di porre fine all'effetto o non usi nuovamente la mossa *Mana* per ottenere prese, questi spiriti faranno in modo che il bersaglio non lasci impronte o altri segni del suo passaggio. Mentre questo incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'crepuscolo' },
    { classId: 'druido' },
    { classId: 'paladino', tradizione: 'natura', requiresArchetype: 'elfo' },
  ],
};

const curareMalattie: Spell = {
  id: 'chierico.curare-malattie',
  name: 'Curare Malattie',
  level: 3,
  tags: [],
  description:
    'Tocchi un alleato (incluso te stesso) e lo curi da una malattia che lo sta affliggendo. Le malattie comuni sono neutralizzate, i sintomi di quelle magiche o estremamente potenti, invece, potrebbero essere solo sospesi per qualche tempo (a discrezione del GM).',
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const favoreDivino: Spell = {
  id: 'chierico.favore-divino',
  name: 'Favore Divino',
  level: 3,
  tags: ['continuato'],
  description:
    "Invochi il favore della tua divinità su qualcun altro, indicando una Caratteristica. Il bersaglio ottiene un bonus di +1 a tutti i tiri che effettua usando il modificatore a quella Caratteristica. L'effetto ha termine se lanci un qualunque altro incantesimo.",
  availableTo: [
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const litaniaDiGuarigione: Spell = {
  id: 'chierico.litania-di-guarigione',
  name: 'Litania di Guarigione',
  level: 3,
  tags: ['continuato'],
  description:
    "Mentre questo incantesimo è attivo, quando curi qualcuno (con mezzi magici o mondani) lo guarisci di +1d8 PF. L'effetto termina non appena intraprendi un'azione aggressiva, oppure se smetti di salmodiare sotto voce la litania (puoi comunque *lanciare incantesimi* non offensivi includendo la loro formula nella preghiera che stai recitando). Finché l'effetto permane, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const resurrezione: Spell = {
  id: 'chierico.resurrezione',
  name: 'Resurrezione',
  level: 3,
  tags: [],
  description:
    'Invochi la tua divinità affinché resusciti un corpo la cui anima sia ancora legata in qualche modo al mondo dei viventi. In tal caso la resurrezione è sempre possibile, ma il GM ti chiederà di soddisfare una, più, o anche tutte le seguenti condizioni:\n\n• ci vorranno giorni/settimane/mesi;\n• dovrai ____;\n• dovrai chiedere aiuto a ____;\n• serviranno molti soldi;\n• dovrai sacrificare ____ per farlo.\n\nIl GM ti dirà anche quali condizioni dovrai soddisfare prima di resuscitare il cadavere e a quali, invece, potrai ottemperare successivamente. In ogni caso, la resurrezione non sarà permanente fino a quando tutte le condizioni non saranno state soddisfatte.',
  availableTo: [
    ...ALL_TRADITIONS.map((t) => ({ classId: 'chierico' as const, tradizione: t })),
    { classId: 'druido' as const },
    ...PALADIN_TRADITIONS_FROM_CHIERICO.map((t) => ({
      classId: 'paladino' as const,
      tradizione: t,
    })),
    { classId: 'paladino' as const, tradizione: 'natura' as const, requiresArchetype: 'elfo' },
  ],
};

/**
 * Description recuperata da Francesco (D3-Q7) — mancante nella "Lista degli
 * Incantesimi" del manuale V1 (gap del manuale, lo spell appare solo nella
 * tabella indice Negromanzia a r. 6203). Da rivalidare quando il manuale
 * sarà completo.
 */
const teschioFluttuante: Spell = {
  id: 'chierico.teschio-fluttuante',
  name: 'Teschio Fluttuante',
  level: 3,
  tags: ['continuato'],
  description:
    "Evochi un teschio semimateriale che puoi muovere con il pensiero e attraverso i cui occhi puoi vedere come se fossero i tuoi. Il teschio svanisce se si allontana oltre distanza *vicino* da te, se subisce danni, oppure se decidi di bandirlo. In quest'ultimo caso puoi, se vuoi, far sì che il teschio emetta un urlo straziante mentre scompare, disorientando per qualche cruciale istante tutti i nemici entro distanza *vicino* da esso. Mentre l'effetto è attivo prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

const visioniAttraversoIlTempo: Spell = {
  id: 'chierico.visioni-attraverso-il-tempo',
  name: 'Visioni Attraverso il Tempo',
  level: 3,
  tags: [],
  description:
    "Hai una visione del futuro attraverso una superficie riflettente che osservi mentre lanci l'incantesimo. Il GM ti svela i dettagli di un evento nefasto a suo piacimento che avrà sicuramente luogo se non interverrai su una specifica questione, e ti dà anche un indizio utile per provare a impedire che la catastrofe si avveri.",
  availableTo: [
    { classId: 'chierico', tradizione: 'illuminazione' },
    // D3-Q5: la tabella Warlock (r. 6211) elenca Visioni Attraverso il
    // Tempo sotto L5, ma il livello canonico nella description (r. 6598)
    // è 3. Il Warlock accede al livello-PG 3, non a 5.
    { classId: 'warlock' },
  ],
};

/** 3 orazioni di livello 0 nuove del Chierico. */
export const chiericoCantrips: readonly Spell[] = [guida, purificare, voceDellaTerra];

/** 13 incantesimi di livello 1 nuovi del Chierico. */
export const chiericoLevel1Spells: readonly Spell[] = [
  armaMagica,
  benedizione,
  controllareNonMorti,
  coraggio,
  curareFeriteLeggere,
  curareFeriteModerate,
  curareVeleni,
  incuterePaura,
  individuareAllineamento,
  parlareConIMorti,
  santuario,
  scudoUltraterreno,
  toccoSpettrale,
];

/** 10 incantesimi di livello 3 nuovi del Chierico (D3). */
export const chiericoLevel3Spells: readonly Spell[] = [
  animareIMorti,
  auraDiVerita,
  bloccarePersona,
  camminareConGliSpiriti,
  curareMalattie,
  favoreDivino,
  litaniaDiGuarigione,
  resurrezione,
  teschioFluttuante,
  visioniAttraversoIlTempo,
];

// ============================================================================
// Livello 5 — 8 nuovi (D3)
// ============================================================================

const contagio: Spell = {
  id: 'chierico.contagio',
  name: 'Contagio',
  level: 5,
  tags: ['continuato'],
  description:
    "Scegli una creatura vivente che puoi vedere e pronuncia il nome di una malattia. Finché non dissolvi l'incantesimo o non usi la mossa *Mana* per ottenere prese, il bersaglio soffre dei sintomi di quel malanno (a discrezione del GM). Mentre l'effetto è attivo, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

const curareFeriteCritiche: Spell = {
  id: 'chierico.curare-ferite-critiche',
  name: 'Curare Ferite Critiche',
  level: 5,
  tags: [],
  description:
    'Guarisci te stesso o un alleato che stai toccando di 3d8 PF. Le ferite cicatrizzano miracolosamente, il dolore che causavano svanisce e le emorragie sono interrotte.',
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const esilio: Spell = {
  id: 'chierico.esilio',
  name: 'Esilio',
  level: 5,
  tags: ['continuato'],
  description:
    "Una creatura o un oggetto inanimato entro gittata *vicino* viene esiliato in un'innocua sacca dimensionale. Il bersaglio può agire liberamente entro lo spazio in cui è confinato e potrebbe fuggire con il teletrasporto o lo spostamento planare. Se lanci un incantesimo, perdi di vista il punto di lancio, o revochi l'Esilio, l'effetto termina e il bersaglio torna dove si trovava originariamente (o nelle immediate vicinanze se quello spazio è occupato).",
  availableTo: [{ classId: 'chierico', tradizione: 'crepuscolo' }, { classId: 'warlock' }],
};

const ferire: Spell = {
  id: 'chierico.ferire',
  name: 'Ferire',
  level: 5,
  tags: [],
  description:
    "Scateni l'ira del tuo dio su un nemico che tocchi (o di cui sfiori l'essenza, come nel caso di un fantasma), infliggendogli 2d8 danni *ignora armatura*, *a prescindere dalla sua mole, difese o natura*. Ma anche tu devi pagarne il prezzo: subisci 1d6 danni con etichetta *ignora armatura*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const guardianoDellaFede: Spell = {
  id: 'chierico.guardiano-della-fede',
  name: 'Guardiano della Fede',
  level: 5,
  tags: ['continuato'],
  description:
    "Scegli un alleato (anche te stesso) che sia impegnato in combattimento. Appare una creatura semimateriale emanazione del dio che veneri (descrivila) che si pone a protezione del bersaglio. Ogni volta che questi viene attaccato, puoi scegliere un'opzione dalla mossa *difendere* come se avessi speso una presa tu stesso. Se scegli l'opzione «*Subisci le conseguenze dell'azione ostile al posto di ciò che stai difendendo*», il bersaglio evita qualsiasi danno o effetto, ma il Guardiano svanisce e non potrai evocarlo nuovamente finché non usi la mossa *Mana* per ottenere prese. L'incantesimo finisce anche se decidi di porvi termine o se il bersaglio smette di combattere, ma, in questi casi, puoi lanciarlo ancora nel corso della giornata. Puoi avere un solo Guardiano attivo alla volta, e finché l'effetto permane, prendi -1 continuato a *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const ripristinare: Spell = {
  id: 'chierico.ripristinare',
  name: 'Ripristinare',
  level: 5,
  tags: [],
  description:
    'Tocchi un alleato (incluso te stesso) e preghi la tua divinità per qualche minuto affinché ripristini le piene funzionalità di un singolo organo non vitale o di un arto, purché non siano stati recisi o compromessi da troppo tempo. In alternativa, puoi anche contrastare un singolo effetto che altera la forma o la materia di un corpo, come, ad esempio, pietrificazione o metamorfosi. In questo caso, gli effetti più deboli sono annullati, quelli più potenti (a discrezione del GM) solo sospesi per qualche tempo.',
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const rivelazioni: Spell = {
  id: 'chierico.rivelazioni',
  name: 'Rivelazioni',
  level: 5,
  tags: [],
  description:
    'Preghi la tua divinità affinché faccia luce su una specifica situazione. Il GM ti illustrerà i fatti oggettivi, ma non ti dirà le motivazioni. Quando agisci seguendo la rivelazione, prendi +1 al prossimo tiro. Tuttavia, se abusi di questo potere, la luce della verità potrebbe brillare anche su di te, rivelando i tuoi piani e i tuoi segreti a qualcuno che sarebbe stato meglio non li avesse conosciuti.',
  availableTo: [{ classId: 'chierico', tradizione: 'illuminazione' }],
};

const voceDeiTaciturni: Spell = {
  id: 'chierico.voce-dei-taciturni',
  name: 'Voce dei Taciturni',
  level: 5,
  tags: [],
  description:
    'La tua divinità o il tuo patrono ti concedono di percepire eco dal passato di un oggetto inanimato che tocchi, o di un luogo in cui ti trovi. Il GM risponderà in modo veritiero a tre domande che poni, tenendo conto delle esperienze che l\'oggetto o il luogo hanno "vissuto" attraverso i loro "sensi".',
  availableTo: [{ classId: 'chierico', tradizione: 'illuminazione' }, { classId: 'warlock' }],
};

/** 8 incantesimi di livello 5 nuovi del Chierico (D3). */
export const chiericoLevel5Spells: readonly Spell[] = [
  contagio,
  curareFeriteCritiche,
  esilio,
  ferire,
  guardianoDellaFede,
  ripristinare,
  rivelazioni,
  voceDeiTaciturni,
];

// ============================================================================
// Livello 7 — 6 nuovi (D4)
// ============================================================================

const camminareNelleOmbre: Spell = {
  id: 'chierico.camminare-nelle-ombre',
  name: 'Camminare nelle Ombre',
  level: 7,
  tags: ['continuato'],
  description:
    "Indica un luogo che conosci bene (a discrezione del GM) e un'ombra che tocchi diventa un portale unidirezionale verso tale destinazione. Tu e i tuoi alleati presenti al momento del lancio potete attraversare il portale per giungere istantaneamente nel luogo indicato. L'effetto termina dopo che tu e ciascuno degli alleati lo avrete varcato, oppure se lanci nuovamente questo incantesimo. Finché il portale resta aperto, prendi -1 continuato a *lanciare incantesimi*. Se fai ricorso a questa magia più volte in un giorno, preparati a ricevere attenzioni da parte di coloro che dimorano nelle tenebre!",
  availableTo: [{ classId: 'chierico', tradizione: 'crepuscolo' }],
};

const divinazione: Spell = {
  id: 'chierico.divinazione',
  name: 'Divinazione',
  level: 7,
  tags: [],
  description:
    "Nomina una persona, un luogo o un oggetto su cui vorresti saperne di più, ed entri in uno stato di trance profonda. Per qualche minuto (uno o due) la tua divinità ti concede una visione del bersaglio, chiara come se fossi lì presente. Scegli se vuoi vedere il presente o un momento significativo (a discrezione del GM) dell'ultimo giorno. Se vieni disturbato o ferito, la visione ha immediatamente termine.",
  availableTo: [{ classId: 'chierico', tradizione: 'illuminazione' }],
};

const guarigione: Spell = {
  id: 'chierico.guarigione',
  name: 'Guarigione',
  level: 7,
  tags: [],
  description:
    "Guarisci un numero di danni pari al tuo valore massimo di PF a un alleato che tocchi o a te stesso. Questo incantesimo non è in grado di ripristinare un arto amputato o un organo compromesso, ma tutte le altre ferite cicatrizzano all'istante, le ossa si rinsaldano, le emorragie sono interrotte e il dolore sparisce.",
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const ispirazioneDivina: Spell = {
  id: 'chierico.ispirazione-divina',
  name: 'Ispirazione Divina',
  level: 7,
  tags: ['continuato'],
  description:
    "Un alleato che puoi vedere (incluso te stesso) riceve ispirazione dalla tua divinità e ottiene una presa che può usare al posto di una singola presa di qualsiasi sua mossa gli dia la possibilità di spenderne. L'effetto ha termine quando la presa viene usata, oppure se usi la mossa *Mana* per ottenere prese. Se lanci di nuovo questo incantesimo prima che la presa venga spesa, essa viene trasferita al nuovo bersaglio.\n\nRicorda, però: alcune mosse hanno dei requisiti narrativi per poter usare le prese. Con *difendere*, ad esempio, devi attivamente proteggere il bersaglio, e con *Mutaforma* (Druido) devi essere trasformato per poterlo fare.",
  availableTo: [{ classId: 'chierico', tradizione: 'illuminazione' }],
};

const pioggiaDiArmi: Spell = {
  id: 'chierico.pioggia-di-armi',
  name: 'Pioggia di Armi',
  level: 7,
  tags: [],
  description:
    "Scagli la tua arma, e decine di sue repliche effimere colpiscono un bersaglio che puoi vedere e tutti gli altri nemici entro portata *lunga* da esso. Tira una sola volta il tuo Danno e applica il risultato, comprensivo di eventuali bonus al danno ma senza altri effetti dovuti a mosse o alle etichette dell'arma, a ognuno dei bersagli. L'arma resta dove l'hai lanciata e le sue repliche svaniscono subito dopo aver colpito.",
  availableTo: [
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

const sigilloDiMorte: Spell = {
  id: 'chierico.sigillo-di-morte',
  name: 'Sigillo di Morte',
  level: 7,
  tags: ['continuato'],
  description:
    "Ripeti il vero nome di una creatura mortale mentre verghi un sigillo su una qualunque superficie solida. Se mai la vittima designata dovesse guardare attentamente il simbolo, essa muore istantaneamente e il sigillo scompare. L'incantesimo termina anche se decidi di porvi fine o se lo lanci nuovamente.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

// ============================================================================
// Livello 9 — 5 nuovi (D4)
// ============================================================================

const consapevolezzaFatale: Spell = {
  id: 'chierico.consapevolezza-fatale',
  name: 'Consapevolezza Fatale',
  level: 9,
  tags: ['continuato'],
  description:
    "Ogni volta che una creatura ti guarda negli occhi, se vuoi, puoi mostrarle una visione della sua morte, ma l'effetto non potrà colpire lo stesso bersaglio più di una volta per lancio dell'incantesimo. Sta a te descrivere una visione credibile per la creatura, ed essa agirà di conseguenza (a discrezione del GM). Questo effetto ha termine se lanci un altro incantesimo, se decidi di porvi fine, o se usi la mossa *Mana* per ottenere prese.",
  availableTo: [{ classId: 'chierico', tradizione: 'crepuscolo' }],
};

const consumareNonMorti: Spell = {
  id: 'chierico.consumare-non-morti',
  name: 'Consumare Non Morti',
  level: 9,
  tags: [],
  description:
    "Assorbi l'energia da una o più creature non morte e non senzienti che si trovano entro portata *lunga* da te. Se il bersaglio è uno, lo distruggi sul colpo. Se i bersagli sono più di uno, sottrai loro fino a un massimo di 20 PF complessivi suddivisi come vuoi (questi danni hanno l'etichetta *ignora armatura*). Puoi usare l'energia assorbita per guarire, a tua scelta, te stesso o il prossimo alleato che tocchi. La quantità di PF curati è pari a quelli che aveva la singola creatura non morta prima di essere distrutta, oppure ai danni che hai *effettivamente* inflitto a molteplici bersagli.",
  availableTo: [
    { classId: 'chierico', tradizione: 'negromanzia' },
    { classId: 'paladino', tradizione: 'negromanzia' },
  ],
};

const formaDiLuceDivina: Spell = {
  id: 'chierico.forma-di-luce-divina',
  name: 'Forma di Luce Divina',
  level: 9,
  tags: ['continuato'],
  description:
    "Diventi tu stesso un'emanazione della luce divina: la tua forma non cambia ma il tuo aspetto è trasfigurato dalla nuova essenza (descrivilo). Finché l'effetto permane ottieni i seguenti benefici:\n\n• hai Visione del Vero come da incantesimo omonimo;\n• quando devi tirare+**modificatore a una Caratteristica**, puoi sempre tirare+**SAG**;\n• emani una luce soprannaturale che dissolve l'oscurità attorno a te e ottieni l'etichetta *immune alle tenebre e alle ombre*;\n• per una sola volta nel corso della durata dell'incantesimo, puoi porre una domanda alla tua divinità. Il GM ti risponderà onestamente.\n\nL'effetto termina appena lanci un incantesimo o quando decidi di porvi fine. Se assumi nuovamente la Forma di Luce Divina prima di aver usato la mossa *Mana* per recuperare prese, il tuo corpo e il tuo spirito potrebbero subire gravi conseguenze per lo stress (a discrezione del GM).",
  availableTo: [{ classId: 'chierico', tradizione: 'illuminazione' }],
};

const panacea: Spell = {
  id: 'chierico.panacea',
  name: 'Panacea',
  level: 9,
  tags: [],
  description:
    "Descrivi un singolo evento del passato di una creatura vivente che è al tuo cospetto (cosa, dove e quando). L'evento resta parte della storia, ma ogni sua conseguenza negativa sul bersaglio (danni, veleni, malattie, effetti magici, traumi o tormenti spirituali, ma *non* la morte) viene annullata come se non fosse mai esistita. Giocare con le trame del destino, però, è pericoloso: se ne abusi, il GM può decidere che altri eventi legati a quello descritto mutino drasticamente, riscrivendo la storia in modi imprevisti.",
  availableTo: [{ classId: 'chierico', tradizione: 'guarigione' }],
};

const presenzaDivina: Spell = {
  id: 'chierico.presenza-divina',
  name: 'Presenza Divina',
  level: 9,
  tags: ['continuato'],
  description:
    "Qualunque creatura senziente si trovi entro distanza *vicino* da te senza il tuo esplicito consenso e possa vederti, reagirà all'aura divina che emani fuggendo in preda al terrore o venerandoti. Solo coloro che non temono gli déi, a ragione o per stupidità, resisteranno all'impulso, ma saranno gravati dal peso del divino: se infliggono danno, tirano una volta in più e prendono il risultato peggiore. L'effetto termina se ti sposti dal punto in cui ti trovavi al momento del lancio. Mentre l'incantesimo è attivo, prendi -1 continuato a *lanciare incantesimi*. Emanare più di una volta al giorno la Presenza Divina richiede un sacrificio: dalla seconda volta dovrai spendere 1 presa di *Mana* ogni volta, prima di tirare per *lanciare incantesimi*.",
  availableTo: [
    { classId: 'chierico', tradizione: 'valore' },
    { classId: 'paladino', tradizione: 'valore' },
  ],
};

/** 6 incantesimi di livello 7 nuovi del Chierico (D4). */
export const chiericoLevel7Spells: readonly Spell[] = [
  camminareNelleOmbre,
  divinazione,
  guarigione,
  ispirazioneDivina,
  pioggiaDiArmi,
  sigilloDiMorte,
];

/** 5 incantesimi di livello 9 nuovi del Chierico (D4). */
export const chiericoLevel9Spells: readonly Spell[] = [
  consapevolezzaFatale,
  consumareNonMorti,
  formaDiLuceDivina,
  panacea,
  presenzaDivina,
];

/** Tutti gli spell nuovi del Chierico trascritti (L0 + L1 + L3 + L5 + L7 + L9). */
export const chiericoSpells: readonly Spell[] = [
  ...chiericoCantrips,
  ...chiericoLevel1Spells,
  ...chiericoLevel3Spells,
  ...chiericoLevel5Spells,
  ...chiericoLevel7Spells,
  ...chiericoLevel9Spells,
];
